/**
 * login_controller.js — sign-in form behaviour (Stimulus).
 *
 * Scoped to the login <form>. Handles three things the page used to wire with
 * end-of-body getElementById + addEventListener (which break / stack after a Turbo
 * <body> swap): show/hide password, the submit spinner, and the passwordless
 * "email me a sign-in link" POST. Also surfaces a server-supplied ?error= message
 * on connect (replacing a conditional inline <script>).
 *
 * Config is read from the form's data-* attributes (SSOT): data-magic-link-url and
 * optional data-error-message. The form itself keeps data-turbo="false" — a
 * successful sign-in 302s into the authenticated shell (different tracked assets),
 * so submit is intentionally a full navigation; the spinner shows until unload.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            var error = this.element.dataset.errorMessage;
            if (error && typeof window.showNotification === 'function') {
                window.showNotification(error, 'error');
            }
        }

        togglePassword(event) {
            var input = this.element.querySelector('#login_password');
            var icon = this.element.querySelector('#login_toggle_password_icon');
            var button = event.currentTarget;
            if (!input) return;

            if (input.type === 'password') {
                input.type = 'text';
                if (icon) icon.className = 'bi bi-eye-slash';
                if (button) button.title = 'Hide password';
            } else {
                input.type = 'password';
                if (icon) icon.className = 'bi bi-eye';
                if (button) button.title = 'Show password';
            }
        }

        onSubmit() {
            var btn = this.element.querySelector('#loginBtn');
            var spinner = this.element.querySelector('#loginSpinner');
            if (!btn) return;
            btn.disabled = true;
            if (spinner) spinner.classList.remove('d-none');
            btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Signing in...';
        }

        sendMagicLink(event) {
            event.preventDefault();
            var emailInput = this.element.querySelector('#login_email');
            var email = emailInput ? emailInput.value.trim() : '';
            if (!email) {
                if (window.showNotification) window.showNotification('Enter your email above first.', 'warning');
                if (emailInput) emailInput.focus();
                return;
            }

            var url = this.element.dataset.magicLinkUrl;
            if (!url) return;

            // The public shell carries no csrf-token meta and no fetch wrapper, so
            // this raw POST delivers the token from the login form's hidden field.
            var tokenField = this.element.querySelector('input[name="csrf_token"]');
            var csrfToken = tokenField ? tokenField.value : '';

            var link = event.currentTarget;
            var originalText = link.textContent;
            link.style.pointerEvents = 'none';
            link.textContent = 'Sending...';

            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: 'email=' + encodeURIComponent(email) + '&csrf_token=' + encodeURIComponent(csrfToken),
                credentials: 'same-origin'
            })
                .then(function (r) { return r.json(); })
                .then(function (data) {
                    link.style.pointerEvents = '';
                    link.textContent = originalText;
                    if (window.showNotification) {
                        window.showNotification(data.message || 'If an account exists, check your email for a sign-in link.', 'info', 8000);
                    }
                })
                .catch(function () {
                    link.style.pointerEvents = '';
                    link.textContent = originalText;
                    if (window.showNotification) window.showNotification('Something went wrong. Please try again.', 'error');
                });
        }
}
